import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { MessageCircle, Bell, Zap, LogOut, User, Settings, LayoutDashboard, Wallet, Plus, AlertTriangle, Shield, Bookmark, Newspaper, PenLine, List, Edit3, Sparkles } from 'lucide-react';
import { useState, useRef, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import PurchaseCreditsModal from '@/components/PurchaseCreditsModal';
import SendCreditsModal from '@/components/SendCreditsModal';
import { useIntent } from '@/components/IntentSheet';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';

// ── Wallet Context: shared across the app ──
const WalletContext = createContext({ balance: 0, refresh: () => {}, openPurchaseModal: () => {}, isLowBalance: false });
export const useWallet = () => useContext(WalletContext);

export function WalletProvider({ children }) {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [creditLimit, setCreditLimit] = useState(0);
  const [availableCredit, setAvailableCredit] = useState(0);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [purchaseTrigger, setPurchaseTrigger] = useState(null);
  const [alertConfig, setAlertConfig] = useState({ low_balance_threshold: 50, critical_balance_threshold: 10, popup_enabled: true, show_low_balance_banner: true, show_topup_on_insufficient: true });

  const refresh = useCallback(async () => {
    if (!user) { setBalance(0); setCreditLimit(0); setAvailableCredit(0); return; }
    try {
      const res = await axios.get(`${API}/credits/wallet`);
      setBalance(res.data?.balance || 0);
      setCreditLimit(res.data?.credit_limit || 0);
      setAvailableCredit(res.data?.available_credit || 0);
    } catch { setBalance(0); setCreditLimit(0); setAvailableCredit(0); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Fetch role-specific alert config
  useEffect(() => {
    if (!user?.role) return;
    axios.get(`${API}/plan-bundles/alert-settings/${user.role}`)
      .then(res => setAlertConfig(res.data))
      .catch(() => {});
  }, [user?.role]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [user, refresh]);

  const openPurchaseModal = useCallback((trigger = null) => {
    setPurchaseTrigger(trigger);
    setShowPurchaseModal(true);
  }, []);

  const closePurchaseModal = useCallback(() => {
    setShowPurchaseModal(false);
    setPurchaseTrigger(null);
  }, []);

  const openSendModal = useCallback(() => setShowSendModal(true), []);
  const closeSendModal = useCallback(() => setShowSendModal(false), []);

  const popupsEnabled = alertConfig.popup_enabled !== false;
  const isLowBalance = user && user.role !== 'admin' && popupsEnabled && alertConfig.show_low_balance_banner !== false && balance < (alertConfig.low_balance_threshold ?? 50) && balance >= 0;
  const isCriticalBalance = user && user.role !== 'admin' && popupsEnabled && balance < (alertConfig.critical_balance_threshold ?? 10) && balance >= 0;

  return (
    <WalletContext.Provider value={{ balance, creditLimit, availableCredit, refresh, openPurchaseModal, openSendModal, isLowBalance, isCriticalBalance, alertConfig }}>
      {children}
      {popupsEnabled && (
        <PurchaseCreditsModal
          open={showPurchaseModal}
          onClose={closePurchaseModal}
          trigger={purchaseTrigger}
        />
      )}
      <SendCreditsModal open={showSendModal} onClose={closeSendModal} />
    </WalletContext.Provider>
  );
}

export default function TopNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { balance, creditLimit, availableCredit, openPurchaseModal, isLowBalance, isCriticalBalance } = useWallet();
  const { isEnabled } = usePlatformFlags();
  const location = useLocation();
  const isDashboard = ['/dashboard', '/admin', '/agent-dashboard', '/institution'].some(p => location.pathname.startsWith(p))
    || location.pathname === '/agent';
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Poll unread count
  useEffect(() => {
    if (!user) return;
    const isAdmin = user.role === 'admin';
    const endpoint = isAdmin ? `${API}/admin/notifications/unread` : `${API}/notifications/unread`;
    const fetchUnread = () => {
      axios.get(endpoint).then(res => {
        setUnreadCount(res.data?.total_unread || 0);
      }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const openNotifs = async () => {
    setShowNotifs(!showNotifs);
    setShowDropdown(false);
    if (!showNotifs) {
      try {
        const isAdmin = user?.role === 'admin';
        const recentEndpoint = isAdmin ? `${API}/admin/notifications/recent` : `${API}/notifications/recent`;
        const markEndpoint = isAdmin ? `${API}/admin/notifications/mark-read` : `${API}/notifications/mark-read`;
        const res = await axios.get(recentEndpoint);
        setNotifications(res.data || []);
        await axios.post(markEndpoint);
        setUnreadCount(0);
      } catch {}
    }
  };

  const handleLogout = async () => {
    setShowDropdown(false);
    await logout();
    navigate('/auth');
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <header className="navbar-glass sticky top-0 z-40" data-testid="top-nav">
      <div className="flex items-center gap-3 px-4 h-14">
        {/* Logo */}
        <Link to="/flow" className="flex items-center gap-2 shrink-0" data-testid="app-logo">
          <div className="w-8 h-8 rounded-xl bg-[#0A2540] flex items-center justify-center">
            <Zap className="h-5 w-5 text-white" fill="currentColor" />
          </div>
          <span className="text-lg font-bold font-heading tracking-tight text-[#0A2540]">Unexa</span>
        </Link>

        <div className="flex-1" />

        {/* Wallet Widget — only on dashboard pages */}
        {user && user.role !== 'admin' && isDashboard && (
          <button
            onClick={() => {
              if (user.role === 'student') navigate('/dashboard?page=wallet');
              else openPurchaseModal();
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold transition-all active:scale-95 mr-1 ${
              isCriticalBalance ? 'bg-red-600 hover:bg-red-700 animate-pulse' :
              isLowBalance ? 'bg-amber-600 hover:bg-amber-700' :
              balance < 0 ? 'bg-orange-600 hover:bg-orange-700' :
              'bg-[#0A2540] hover:bg-[#0D2F4F]'
            }`}
            data-testid="wallet-widget"
            title={creditLimit > 0 ? `Balance: ${balance} cr | Credit Line: ${creditLimit} cr | Available: ${availableCredit} cr` : `Balance: ${balance} cr`}
          >
            {isCriticalBalance ? (
              <AlertTriangle className="h-3.5 w-3.5 text-red-200" />
            ) : isLowBalance ? (
              <AlertTriangle className="h-3.5 w-3.5 text-amber-200" />
            ) : balance < 0 ? (
              <AlertTriangle className="h-3.5 w-3.5 text-orange-200" />
            ) : (
              <Wallet className="h-3.5 w-3.5 text-amber-400" />
            )}
            <span>{balance < 0 ? balance.toLocaleString() : balance.toLocaleString()}</span>
            <span className="text-[10px] text-white/50 font-semibold">cr</span>
            {creditLimit > 0 && (
              <>
                <span className="w-px h-3 bg-white/20 mx-0.5" />
                <span className="text-[10px] text-emerald-300">{availableCredit.toLocaleString()}</span>
              </>
            )}
            <span className="w-px h-3 bg-white/20 mx-0.5" />
            <Plus className="h-3 w-3 text-emerald-400" />
          </button>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Profile Strength chip — passive nudge for students. Hidden when
              fully optimised. Clicking opens the conversational IntentSheet. */}
          {user?.role === 'student' && <IntentStrengthChip />}

          {/* Notification Bell */}
          <div className="relative" ref={notifRef}>
            <button onClick={openNotifs}
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#0A2540] hover:bg-slate-100 transition-all active:scale-90 relative"
              data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#EB5757] text-white text-[9px] font-bold" data-testid="notif-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotifs && (
              <div className="absolute right-0 top-11 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-testid="notif-dropdown">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-sm font-bold text-[#0A2540]">Notifications</p>
                  {notifications.length > 0 && (
                    <span className="text-caption text-[#65676B]">{notifications.filter(n => !n.is_read).length} new</span>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center">
                      <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                      <p className="text-caption text-[#65676B]">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((n, i) => {
                      const handleNotifClick = () => {
                        setShowNotifs(false);
                        const t = n.type || '';
                        if (user?.role === 'admin') { navigate('/admin'); }
                        else if (t === 'like' || t === 'comment' || t === 'reply' || t === 'repost') { navigate('/flow'); }
                        else if (t === 'follow') { navigate(`/user/${n.ref_id}`); }
                        else if (t === 'message') { navigate('/chat'); }
                        else if (t === 'qa_answered') { navigate(n.ref_id ? `/blog/${n.ref_id}` : '/blog?tab=qa'); }
                        else { navigate('/flow'); }
                      };
                      const getIcon = () => {
                        const t = n.type || '';
                        if (t === 'like') return '❤️';
                        if (t === 'comment' || t === 'reply') return '💬';
                        if (t === 'follow') return '👤';
                        if (t === 'repost') return '🔁';
                        if (t === 'message') return '✉️';
                        if (t === 'qa_answered') return '✅';
                        return '🔔';
                      };
                      return (
                        <div key={n.message_id || n.notification_id || i}
                          className={`px-4 py-3 border-b border-gray-50 hover:bg-slate-50 transition-colors cursor-pointer ${!n.is_read && !n.read ? 'bg-blue-50/40' : ''}`}
                          onClick={handleNotifClick}
                          data-testid={`notif-${n.message_id || n.notification_id || i}`}>
                          <div className="flex items-start gap-2.5">
                            <span className="text-sm mt-0.5">{getIcon()}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#0A2540] leading-relaxed">{n.content}</p>
                              <p className="text-caption text-[#65676B] mt-1">{timeAgo(n.created_at)}</p>
                            </div>
                            {(!n.is_read && !n.read) && <div className="w-2 h-2 rounded-full bg-[#2F80ED] mt-2 shrink-0" />}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="px-4 py-2.5 border-t border-gray-100">
                    <button onClick={() => { setShowNotifs(false); navigate('/chat'); }}
                      className="text-sm font-semibold text-[#2F80ED] hover:text-[#1A6BD4] transition-colors"
                      data-testid="view-all-notifs">
                      View all messages
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => { setShowDropdown(!showDropdown); setShowNotifs(false); }}
                className="flex items-center" data-testid="user-avatar-btn">
                {user.profile_picture ? (
                  <img src={user.profile_picture} alt=""
                    className="w-8 h-8 rounded-full border-2 border-transparent hover:border-[#2F80ED] transition-all" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-[#2F80ED] text-sm font-bold hover:bg-blue-100 transition-all">
                    {user.name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
              </button>

              {showDropdown && (
                <div className="absolute right-0 top-11 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="user-dropdown">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-sm font-bold text-[#0A2540] truncate">{user.name}</p>
                    <p className="text-[11px] text-[#6B7280] truncate">{user.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-blue-50 text-[#2F80ED] text-[10px] font-bold capitalize">{user.role}</span>
                  </div>
                  <div className="py-1">
                    <DropdownItem icon={User} label="My Profile" onClick={() => { setShowDropdown(false); navigate(user.role === 'agent' ? `/agents/${user.user_id}` : user.role === 'institution' ? `/university/${user.university_id}` : '/profile'); }} testId="dropdown-profile" />
                    {user.role === 'student' && <DropdownItem icon={LayoutDashboard} label="Dashboard" onClick={() => { setShowDropdown(false); navigate('/dashboard'); }} testId="dropdown-dashboard" />}
                    {user.role === 'admin' && <DropdownItem icon={LayoutDashboard} label="Admin Panel" onClick={() => { setShowDropdown(false); navigate('/admin'); }} testId="dropdown-admin" />}
                    {user.role === 'institution' && <DropdownItem icon={LayoutDashboard} label="Dashboard" onClick={() => { setShowDropdown(false); navigate('/institution'); }} testId="dropdown-institution" />}
                    {user.role === 'institution' && <DropdownItem icon={Edit3} label="Edit Profile" onClick={() => { setShowDropdown(false); navigate('/institution?tab=profile'); }} testId="dropdown-edit-profile" />}
                    {user.role === 'agent' && <DropdownItem icon={LayoutDashboard} label="Dashboard" onClick={() => { setShowDropdown(false); navigate('/agent'); }} testId="dropdown-agent" />}
                    {/* Quick-access items moved here from the profile page Quick Actions panel */}
                    {user.role !== 'admin' && (
                      <>
                        <DropdownItem icon={Bookmark} label="Saved Posts" onClick={() => { setShowDropdown(false); navigate('/saved'); }} testId="dropdown-saved" />
                        <DropdownItem icon={Newspaper} label="Newsboard" onClick={() => { setShowDropdown(false); navigate('/blog?tab=news'); }} testId="dropdown-newsboard" />
                        {user.role !== 'institution' && (
                          <>
                            <DropdownItem icon={PenLine} label="My Blogs" onClick={() => { setShowDropdown(false); navigate('/blog'); }} testId="dropdown-myblogs" />
                            {isEnabled('listings') && (
                              <DropdownItem icon={List} label="Listings" onClick={() => { setShowDropdown(false); navigate('/listings'); }} testId="dropdown-listings" />
                            )}                          </>
                        )}
                      </>
                    )}
                    <DropdownItem icon={Shield} label="Privacy & Safety" onClick={() => { setShowDropdown(false); navigate('/privacy-settings'); }} testId="dropdown-privacy" />
                  </div>
                  <div className="border-t border-gray-100 pt-1">
                    <DropdownItem icon={LogOut} label="Log Out" onClick={handleLogout} danger testId="dropdown-logout" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth"
              className="px-4 py-1.5 rounded-full bg-[#2F80ED] text-white text-xs font-bold hover:bg-[#1A6BD4] transition-all active:scale-95"
              data-testid="topnav-login-btn">
              Sign In
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function DropdownItem({ icon: Icon, label, onClick, danger, testId }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${danger ? 'text-[#EB5757] hover:bg-red-50' : 'text-[#1A1A1A]'}`}
      data-testid={testId}>
      <Icon className="h-4 w-4" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

/**
 * IntentStrengthChip — a small, passive pill in the header showing the
 * student's current intent profile strength (0-100%). Replaces the old
 * blocking popup. Clicking opens the conversational IntentSheet.
 *
 * Render rules:
 *   • Only for students
 *   • Hidden if score is 100% (Strong+Verified)
 *   • Lazily fetches /api/intent/strength once on mount, refreshes every 5 min
 */
function IntentStrengthChip() {
  const { openIntentSheet } = useIntent();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API}/intent/strength`);
        if (alive) setData(res.data);
      } catch { /* not logged in */ }
    };
    fetchIt();
    const id = setInterval(fetchIt, 300000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!data || data.score >= 100) return null;

  // Colour ramp — Strong = green, Good = teal, Basic = amber, Incomplete = grey.
  const palette = data.score >= 90 ? { bg: '#1A7A6E', text: '#FFFFFF' }
    : data.score >= 70 ? { bg: '#1A7A6E', text: '#FFFFFF' }
    : data.score >= 40 ? { bg: '#FFF7E6', text: '#92400E' }
    : { bg: '#F1F5F9', text: '#475569' };

  return (
    <button
      onClick={() => openIntentSheet({ action: 'apply' })}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold transition-all hover:shadow-sm active:scale-95"
      style={{ backgroundColor: palette.bg, color: palette.text }}
      title={`Profile strength: ${data.level} · ${data.score}%`}
      data-testid="intent-strength-chip"
    >
      <Sparkles className="h-3 w-3" />
      <span>{data.score}%</span>
    </button>
  );
}
